import { useEffect, useMemo, useRef, useState } from 'react';
import {
  VizCard,
  NowYouTry,
  VIZ_FOCUS,
  usePrefersReducedMotion,
  useMountSequence,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from '../vizShared';

// SolubilityCurve — the classic gen-chem solubility chart: grams of solute that
// dissolve per 100 g of water plotted against temperature (0–100 °C). Each salt
// is a smooth, labelled, distinctly-coloured curve. Most ionic solids climb with
// temperature (more soluble when hot); a dissolved GAS like ammonia descends
// (less soluble when hot). A "read-off" guide can be dropped at any temperature
// to mark each curve's saturation value there — the everyday textbook question
// "how much KNO3 dissolves at 60 °C?".
//
// config: { salts?: string[], readTemp?: number, readSalt?: string, title? }
// Cue: cue.highlight a salt NAME spotlights that curve (others dim); cue.step
// indexes the listed salts to spotlight in turn; cue.progress (0–1) sweeps the
// vertical read-line from 0 → 100 °C. Reduced motion → full static chart.

export interface SolubilityCurveConfig {
  /** Which curves to draw. Unknown names are ignored; empty ⇒ default trio. */
  salts?: string[];
  /** Draw a vertical guide at this °C and label each curve's value there. */
  readTemp?: number;
  /** Spotlight one curve, dimming the rest. */
  readSalt?: string;
  title?: string;
}

interface Props {
  config?: SolubilityCurveConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

// ── Built-in textbook data: grams per 100 g water at these temperatures ───────
const TEMPS = [0, 20, 40, 60, 80, 100] as const;

interface SaltDatum {
  key: string; // canonical name
  label: string; // pretty label (subscripts handled at render)
  data: number[]; // g / 100 g water at TEMPS
  gas?: boolean; // dissolved gas → solubility falls with temperature
}

const SALTS: SaltDatum[] = [
  { key: 'KNO3', label: 'KNO₃', data: [13, 32, 64, 110, 169, 246] },
  { key: 'NaCl', label: 'NaCl', data: [36, 36, 37, 37, 38, 39] },
  { key: 'KCl', label: 'KCl', data: [28, 34, 40, 46, 51, 57] },
  { key: 'NaNO3', label: 'NaNO₃', data: [73, 88, 104, 124, 148, 180] },
  { key: 'KClO3', label: 'KClO₃', data: [3, 7, 14, 24, 38, 57] },
  { key: 'Pb(NO3)2', label: 'Pb(NO₃)₂', data: [38, 56, 75, 95, 116, 138] },
  { key: 'CaCl2', label: 'CaCl₂', data: [60, 75, 92, 115, 137, 160] },
  { key: 'NH3', label: 'NH₃', data: [90, 56, 34, 20, 12, 7], gas: true },
];

const SALT_BY_KEY = new Map(SALTS.map((s) => [s.key.toLowerCase(), s]));

// Tolerant lookup: accept differing case, spaces, and a couple of name aliases.
function resolveSalt(token: string): SaltDatum | null {
  const raw = token.trim();
  if (!raw) return null;
  const norm = raw.toLowerCase().replace(/\s+/g, '');
  const direct = SALT_BY_KEY.get(norm);
  if (direct) return direct;
  const aliases: Record<string, string> = {
    'potassiumnitrate': 'kno3',
    'sodiumchloride': 'nacl',
    salt: 'nacl',
    'potassiumchloride': 'kcl',
    'sodiumnitrate': 'nano3',
    'potassiumchlorate': 'kclo3',
    'leadnitrate': 'pb(no3)2',
    'lead(ii)nitrate': 'pb(no3)2',
    'calciumchloride': 'cacl2',
    ammonia: 'nh3',
    'ammoniagas': 'nh3',
  };
  const aliased = aliases[norm];
  return aliased ? SALT_BY_KEY.get(aliased) ?? null : null;
}

const DEFAULT_SALTS = ['KNO3', 'NaCl', 'KClO3'];

// Distinct, theme-aware curve colours drawn from the semantic token set so they
// re-tint per theme and stay legible. Assigned in order of the drawn salts.
const CURVE_COLORS = [
  'var(--ws-accent)',
  'var(--ws-danger)',
  'var(--ws-success)',
  'var(--ws-warning)',
  'var(--ws-dark-teal)',
  'var(--ws-accent-hover)',
];

// ── SVG plot geometry (house style shared with EnergyProfile / TitrationCurve) ─
const PLOT_W = 360;
const PLOT_H = 250;
const PAD_L = 38;
const PAD_R = 64; // room for end-of-curve labels
const PAD_T = 16;
const PAD_B = 32;

const T_MIN = 0;
const T_MAX = 100;

function num(n: unknown, fallback: number): number {
  const v = typeof n === 'number' ? n : NaN;
  return Number.isFinite(v) ? v : fallback;
}

// Monotone-ish smooth interpolation of the 6 anchor points at an arbitrary
// temperature, using a Catmull-Rom spline (so the curve is smooth but still
// passes through the textbook values). Clamps to the data range endpoints.
function solubilityAt(data: number[], tC: number): number {
  const t = Math.max(T_MIN, Math.min(T_MAX, tC));
  const span = TEMPS.length - 1;
  // segment index within TEMPS
  const seg = Math.min(span - 1, Math.floor((t - T_MIN) / ((T_MAX - T_MIN) / span)));
  const t0 = TEMPS[seg];
  const t1 = TEMPS[seg + 1];
  const localT = (t - t0) / (t1 - t0); // 0..1 within segment
  const p0 = data[Math.max(0, seg - 1)];
  const p1 = data[seg];
  const p2 = data[seg + 1];
  const p3 = data[Math.min(span, seg + 2)];
  // Catmull-Rom basis
  const a = localT;
  const a2 = a * a;
  const a3 = a2 * a;
  const v =
    0.5 *
    (2 * p1 +
      (-p0 + p2) * a +
      (2 * p0 - 5 * p1 + 4 * p2 - p3) * a2 +
      (-p0 + 3 * p1 - 3 * p2 + p3) * a3);
  return Math.max(0, v);
}

export default function SolubilityCurve({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  // ----- Resolve which salts to draw (resilient to junk / empty) -------------
  const salts = useMemo(() => {
    const requested = Array.isArray(config.salts) ? config.salts : undefined;
    const names = requested && requested.length > 0 ? requested : DEFAULT_SALTS;
    const seen = new Set<string>();
    const out: SaltDatum[] = [];
    for (const n of names) {
      const s = resolveSalt(String(n));
      if (s && !seen.has(s.key)) {
        seen.add(s.key);
        out.push(s);
      }
    }
    // Never end up empty (all names were junk) → fall back to the default trio.
    if (out.length === 0) {
      for (const n of DEFAULT_SALTS) {
        const s = resolveSalt(n);
        if (s && !seen.has(s.key)) {
          seen.add(s.key);
          out.push(s);
        }
      }
    }
    return out;
  }, [config.salts]);

  const colorFor = (i: number) => CURVE_COLORS[i % CURVE_COLORS.length];

  // ----- y-axis auto-scale to the shown curves (cap ~250) --------------------
  const yMax = useMemo(() => {
    let peak = 0;
    for (const s of salts) peak = Math.max(peak, ...s.data);
    // round up to a tidy gridline step, capped near 250
    const padded = peak * 1.08;
    const step = padded > 200 ? 50 : padded > 100 ? 25 : padded > 40 ? 20 : 10;
    const rounded = Math.ceil(padded / step) * step;
    return Math.min(250, Math.max(step, rounded));
  }, [salts]);

  // ----- Coordinate mappers --------------------------------------------------
  const sx = (tC: number) =>
    PAD_L + ((tC - T_MIN) / (T_MAX - T_MIN)) * (PLOT_W - PAD_L - PAD_R);
  const sy = (g: number) =>
    PAD_T + (1 - Math.min(g, yMax) / yMax) * (PLOT_H - PAD_T - PAD_B);

  // Smooth polyline path sampled across the temperature range.
  const SAMPLES = 60;
  const pathFor = (data: number[]) => {
    let d = '';
    for (let i = 0; i <= SAMPLES; i++) {
      const tC = T_MIN + (i / SAMPLES) * (T_MAX - T_MIN);
      const x = sx(tC);
      const y = sy(solubilityAt(data, tC));
      d += `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)} `;
    }
    return d.trim();
  };

  // ----- Read-off temperature line -------------------------------------------
  // Provided readTemp pins the guide; cue.progress sweeps it; otherwise none.
  const configReadTemp =
    config.readTemp != null
      ? Math.max(T_MIN, Math.min(T_MAX, num(config.readTemp, 60)))
      : null;
  const [sweepT, setSweepT] = useState<number | null>(null); // cue.progress-driven
  const [userT, setUserT] = useState<number | null>(null); // interaction-driven

  // Effective read temperature: user > cue sweep > config.
  const readTemp = userT ?? sweepT ?? configReadTemp;

  // ----- Spotlight one salt (readSalt / cue.highlight / cue.step) -------------
  const initialSpot = useMemo(() => {
    if (!config.readSalt) return null;
    const s = resolveSalt(config.readSalt);
    return s && salts.some((x) => x.key === s.key) ? s.key : null;
  }, [config.readSalt, salts]);

  const [spotKey, setSpotKey] = useState<string | null>(initialSpot);

  // ----- Reveal / self-demo state --------------------------------------------
  // draw 0..1 animates the curves growing in left→right on mount.
  const [draw, setDraw] = useState(reduced || hasCue ? 1 : 0);
  const interactedRef = useRef(false);

  const mark = (result?: unknown) => {
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.(result ?? { type: 'solubility_curve', salts: salts.map((s) => s.key) });
    }
  };

  // Self-demo: a one-step sequence whose only job is to mark `done` (which then
  // shows the "Now you try" pill) once the curve-draw animation has finished.
  // Cancelled by any interaction (legend click / chart click).
  const DRAW_MS = 1100;
  const demo = useMountSequence([{ at: DRAW_MS + 120, run: () => setDraw(1) }], {
    skip: reduced || hasCue,
    onSettle: () => setDraw(1),
  });

  // Animate the curves growing in left→right on mount (rAF on stroke-dashoffset).
  useEffect(() => {
    if (reduced || hasCue) {
      setDraw(1);
      return;
    }
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / DRAW_MS);
      setDraw(p);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // Arm once on mount; reduced/hasCue read at mount time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- Cue handling --------------------------------------------------------
  // highlight a salt name → spotlight it.
  const cueTokens = useMemo(() => cueHighlights(cue), [cue]);
  useEffect(() => {
    if (cueTokens.length === 0) return;
    for (const tok of cueTokens) {
      const s = resolveSalt(tok);
      if (s && salts.some((x) => x.key === s.key)) {
        setSpotKey(s.key);
        return;
      }
    }
  }, [cueTokens, salts]);

  // step indexes the listed salts to spotlight in turn.
  useEffect(() => {
    if (cue?.step == null || salts.length === 0) return;
    const idx = Math.max(0, Math.min(salts.length - 1, Math.round(cue.step)));
    setSpotKey(salts[idx].key);
  }, [cue?.step, salts]);

  // progress sweeps the vertical read-line 0 → 100 °C.
  useEffect(() => {
    if (cue?.progress == null) {
      setSweepT(null);
      return;
    }
    const p = Math.max(0, Math.min(1, cue.progress));
    setSweepT(T_MIN + p * (T_MAX - T_MIN));
  }, [cue?.progress]);

  // ----- y-axis gridline ticks -----------------------------------------------
  const yStep = yMax > 200 ? 50 : yMax > 100 ? 25 : yMax > 40 ? 20 : 10;
  const yTicks = useMemo(() => {
    const ticks: number[] = [];
    for (let g = 0; g <= yMax + 0.5; g += yStep) ticks.push(g);
    return ticks;
  }, [yMax, yStep]);

  const anySpot = spotKey != null;
  const dimOf = (key: string) => (anySpot && key !== spotKey ? 0.18 : 1);

  // ----- Interaction: click the plot to set a read temperature ---------------
  const svgRef = useRef<SVGSVGElement | null>(null);
  const setReadFromClientX = (clientX: number) => {
    const svg = svgRef.current;
    if (!svg) return;
    const r = svg.getBoundingClientRect();
    const frac = (clientX - r.left) / r.width; // 0..1 across the rendered svg
    const xUser = frac * PLOT_W; // into viewBox units
    const plotFrac = (xUser - PAD_L) / (PLOT_W - PAD_L - PAD_R);
    const tC = T_MIN + Math.max(0, Math.min(1, plotFrac)) * (T_MAX - T_MIN);
    if (demo.running) demo.cancel();
    setUserT(Math.round(tC));
    mark({ type: 'solubility_curve', readTemp: Math.round(tC) });
  };

  const readValues =
    readTemp == null
      ? []
      : salts.map((s, i) => ({
          salt: s,
          color: colorFor(i),
          value: solubilityAt(s.data, readTemp),
        }));

  return (
    <VizCard
      eyebrow="Solutions · Solubility vs temperature"
      title={config.title ?? 'Solubility curves'}
      subtitle="Grams of solute per 100 g of water across 0–100 °C"
      reducedMotion={reduced}
      hint="Click the chart to read off saturation at a temperature · most solids dissolve more when hot; the gas (NH₃) less"
    >
      <div className="relative w-full select-none">
        <NowYouTry show={demo.done && !reduced && !hasCue} />

        <svg
          ref={svgRef}
          viewBox={`0 0 ${PLOT_W} ${PLOT_H}`}
          className={`w-full rounded-md border border-ws-border bg-ws-surface-2 ${VIZ_FOCUS}`}
          role="img"
          aria-label={`Solubility curves for ${salts.map((s) => s.key).join(', ')}`}
          onClick={(e) => setReadFromClientX(e.clientX)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
              e.preventDefault();
              if (demo.running) demo.cancel();
              const base = userT ?? readTemp ?? 50;
              const next = Math.max(
                T_MIN,
                Math.min(T_MAX, base + (e.key === 'ArrowRight' ? 5 : -5)),
              );
              setUserT(next);
              mark({ type: 'solubility_curve', readTemp: next });
            }
          }}
        >
          {/* y gridlines + labels */}
          {yTicks.map((g) => (
            <g key={`y${g}`}>
              <line
                x1={PAD_L}
                y1={sy(g)}
                x2={PLOT_W - PAD_R}
                y2={sy(g)}
                stroke="var(--ws-border)"
                strokeWidth="0.6"
                opacity={g === 0 ? 0 : 0.7}
              />
              <text
                x={PAD_L - 5}
                y={sy(g) + 2.5}
                textAnchor="end"
                fontSize="7"
                fill="var(--ws-ink-3)"
              >
                {g}
              </text>
            </g>
          ))}

          {/* x gridlines + labels */}
          {TEMPS.map((tC) => (
            <g key={`x${tC}`}>
              <line
                x1={sx(tC)}
                y1={PAD_T}
                x2={sx(tC)}
                y2={PLOT_H - PAD_B}
                stroke="var(--ws-border)"
                strokeWidth="0.6"
                opacity={tC === 0 ? 0 : 0.4}
              />
              <text
                x={sx(tC)}
                y={PLOT_H - PAD_B + 11}
                textAnchor="middle"
                fontSize="7"
                fill="var(--ws-ink-3)"
              >
                {tC}
              </text>
            </g>
          ))}

          {/* Axes */}
          <line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={PLOT_H - PAD_B} stroke="var(--ws-border-strong)" strokeWidth="1" />
          <line x1={PAD_L} y1={PLOT_H - PAD_B} x2={PLOT_W - PAD_R} y2={PLOT_H - PAD_B} stroke="var(--ws-border-strong)" strokeWidth="1" />

          {/* Axis titles */}
          <text
            x={PAD_L - 28}
            y={PLOT_H / 2}
            textAnchor="middle"
            fontSize="7.5"
            fill="var(--ws-ink-3)"
            transform={`rotate(-90 ${PAD_L - 28} ${PLOT_H / 2})`}
          >
            Solubility (g / 100 g H₂O)
          </text>
          <text
            x={(PAD_L + PLOT_W - PAD_R) / 2}
            y={PLOT_H - 4}
            textAnchor="middle"
            fontSize="7.5"
            fill="var(--ws-ink-3)"
          >
            Temperature (°C)
          </text>

          {/* Read-off vertical guide */}
          {readTemp != null && (
            <g style={{ transition: reduced ? undefined : 'opacity 200ms ease-out' }}>
              <line
                x1={sx(readTemp)}
                y1={PAD_T}
                x2={sx(readTemp)}
                y2={PLOT_H - PAD_B}
                stroke="var(--ws-accent-hover)"
                strokeWidth="1"
                strokeDasharray="3 3"
              />
              <rect
                x={sx(readTemp) - 13}
                y={PAD_T - 1}
                width="26"
                height="11"
                rx="2.5"
                fill="var(--ws-accent-hover)"
              />
              <text
                x={sx(readTemp)}
                y={PAD_T + 6.5}
                textAnchor="middle"
                fontSize="7"
                fontWeight="700"
                fill="var(--ws-on-accent)"
              >
                {Math.round(readTemp)}°C
              </text>
            </g>
          )}

          {/* Curves */}
          {salts.map((s, i) => {
            const color = colorFor(i);
            const isSpot = spotKey === s.key;
            const endX = sx(T_MAX);
            const endY = sy(solubilityAt(s.data, T_MAX));
            return (
              <g
                key={s.key}
                opacity={dimOf(s.key)}
                style={{ transition: reduced ? undefined : 'opacity 250ms ease-out' }}
              >
                <path
                  d={pathFor(s.data)}
                  fill="none"
                  stroke={color}
                  strokeWidth={isSpot ? 2.6 : 1.8}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  pathLength={1}
                  style={{
                    strokeDasharray: 1,
                    strokeDashoffset: reduced ? 0 : 1 - draw,
                    transition: reduced ? undefined : 'stroke-dashoffset 120ms linear',
                  }}
                />
                {/* End-of-line label */}
                <text
                  x={endX + 4}
                  y={endY + 2.5}
                  fontSize="7.5"
                  fontWeight={isSpot ? 700 : 600}
                  fill={color}
                  opacity={draw > 0.85 ? 1 : 0}
                  style={{ transition: reduced ? undefined : 'opacity 200ms ease-out' }}
                >
                  {s.label}
                  {s.gas ? ' (gas)' : ''}
                </text>
              </g>
            );
          })}

          {/* Read-off saturation points + value tags on each curve */}
          {readTemp != null &&
            readValues.map(({ salt, color, value }) => {
              const cx = sx(readTemp);
              const cy = sy(value);
              const isSpot = spotKey === salt.key;
              return (
                <g
                  key={`read-${salt.key}`}
                  opacity={dimOf(salt.key)}
                  style={{ transition: reduced ? undefined : 'opacity 250ms ease-out' }}
                >
                  <circle cx={cx} cy={cy} r={isSpot ? 3.4 : 2.6} fill={color} stroke="var(--ws-surface)" strokeWidth="1.1" />
                  {/* value tag, nudged left of the guide */}
                  <g>
                    <rect
                      x={cx - 30}
                      y={cy - 6}
                      width="26"
                      height="12"
                      rx="2.5"
                      fill="var(--ws-surface)"
                      stroke={color}
                      strokeWidth="0.8"
                    />
                    <text
                      x={cx - 17}
                      y={cy + 2.6}
                      textAnchor="middle"
                      fontSize="7"
                      fontWeight="700"
                      fill="var(--ws-ink)"
                    >
                      {value.toFixed(0)}
                    </text>
                  </g>
                </g>
              );
            })}
        </svg>

        {/* Legend — click to spotlight a curve */}
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {salts.map((s, i) => {
            const active = spotKey === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => {
                  if (demo.running) demo.cancel();
                  setSpotKey((cur) => (cur === s.key ? null : s.key));
                  mark({ type: 'solubility_curve', spotlight: s.key });
                }}
                aria-pressed={active}
                className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-medium transition-colors ${VIZ_FOCUS} ${
                  active
                    ? 'border-ws-accent bg-ws-accent-quiet text-ws-accent-ink'
                    : 'border-ws-border bg-ws-tile text-ws-ink-2 hover:bg-ws-surface-2'
                }`}
              >
                <span
                  className="inline-block h-2 w-3.5 rounded-full"
                  style={{ backgroundColor: colorFor(i) }}
                />
                {s.label}
                {s.gas ? ' · gas' : ''}
              </button>
            );
          })}
          {anySpot && (
            <button
              type="button"
              onClick={() => {
                if (demo.running) demo.cancel();
                setSpotKey(null);
              }}
              className={`rounded-md px-2 py-1 text-[10px] font-medium text-ws-ink-3 hover:text-ws-ink-2 ${VIZ_FOCUS}`}
            >
              clear
            </button>
          )}
        </div>

        {/* Read-off readout */}
        {readTemp != null && readValues.length > 0 && (
          <div className="mt-3 rounded-md border border-ws-border bg-ws-tile px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-ws-ink-3">
              Saturation at {Math.round(readTemp)} °C
            </div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
              {readValues.map(({ salt, color, value }) => (
                <span
                  key={`tag-${salt.key}`}
                  className="inline-flex items-baseline gap-1.5 text-xs"
                  style={{ opacity: anySpot && salt.key !== spotKey ? 0.4 : 1 }}
                >
                  <span className="inline-block h-2 w-2 self-center rounded-full" style={{ backgroundColor: color }} />
                  <span className="font-medium text-ws-ink">{salt.label}</span>
                  <span className="font-mono font-semibold tabular-nums text-ws-ink-2">
                    {value.toFixed(0)} g
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}

        <p className="mt-2 text-[11px] leading-snug text-ws-ink-2">
          A point on a curve is the maximum that dissolves at that temperature — a{' '}
          <span className="font-medium text-ws-ink">saturated</span> solution. Below the
          curve is unsaturated; above it the excess crystallises out.
        </p>
      </div>
    </VizCard>
  );
}
